import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

type Props = {
  clubId: string;
};

export default function FollowButton({ clubId }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) {
      setFollowing(false);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('club_follows')
      .select('id')
      .eq('club_id', clubId)
      .eq('user_id', user.id)
      .maybeSingle();

    setFollowing(!!data);
    setLoading(false);
  }, [clubId, user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleClick = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setSubmitting(true);

    if (following) {
      const { error } = await supabase
        .from('club_follows')
        .delete()
        .eq('club_id', clubId)
        .eq('user_id', user.id);
      if (error) console.error('Follow delete error:', error);
    } else {
      const { error } = await supabase.from('club_follows').insert({
        club_id: clubId,
        user_id: user.id,
      });
      if (error) console.error('Follow insert error:', error);
    }

    await fetchStatus();
    setSubmitting(false);
  };

  if (loading) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={submitting}
      className={`inline-flex w-full items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold transition disabled:opacity-50 ${
        following
          ? 'bg-black text-white hover:bg-black/80'
          : 'border border-black/15 bg-white/80 text-black/70 hover:bg-white'
      }`}
    >
      {following ? 'Following ✓' : 'Follow'}
    </button>
  );
}
